import { BaseState } from '@bimser/common';
import * as LayoutCreatorEntities from '@bimser/components/dist/BCLayoutCreator/common/entities';
import { RuleEngineLegacyEntities } from '@bimser/components/dist/BCRuleManager';
import { IDictionary, RxEventEmitter, isNullOrUndefined } from '@bimser/core';
import { MainState } from '@bimser/studio-core/dist/studioState';
import { Map } from 'immutable';
import { EngineResult } from 'json-rules-engine';
import { select } from 'redux-saga/effects';
import { getControlPropertyValue, getFormItems, getFormulaFactKeys, getPreviewFormItems } from '.';
import { ICheckLegacyRules } from '../../../entities/ICheckRules';
import { IRule, IRuleEngines } from './legacy/entities';
import { convertRules } from './legacy/convertRules';
import { setRuleEngines } from './legacy/setRuleEngines';
import { triggerStatement } from './legacy/triggerStatement';

export function* checkLegacyRules(args: ICheckLegacyRules) {
	let result: boolean = false;

	if (!args.formData) {
		return result;
	}

	if (args.rules.count()) {
		const eliminateNonexistentFieldConditions = (
			_rules: IDictionary<RuleEngineLegacyEntities.IRule>
		) => {
			Object.keys(_rules).forEach(ruleName => {
				if (_rules[ruleName].statements?.length) {
					_rules[ruleName].statements = _rules[ruleName].statements.map(statement => {
						if (statement?.conditionGroup?.conditions.length) {
							statement.conditionGroup.conditions =
								statement.conditionGroup.conditions.filter(condition =>
									args.formData.get(condition.field.name)
								);
						}
						return statement;
					});
				}
			});
			return _rules;
		};
		let immutableRules: IDictionary<RuleEngineLegacyEntities.IRule> = args.rules
			.sort((a, b) => {
				if (a.orderNo < b.orderNo) {
					return -1;
				} else if (a.orderNo > b.orderNo) {
					return 1;
				} else {
					return 0;
				}
			})
			.toJS() as any;
		immutableRules = eliminateNonexistentFieldConditions(immutableRules);
		let allFactKeys: string[] = [];
		const rules: IRule[] = convertRules(immutableRules, args.formData, (factKey: string) => {
			allFactKeys = [...allFactKeys, factKey, ...getFormulaFactKeys(args.formData, factKey)];
		});

		let ruleNodes: Array<IRule[]> = [],
			ruleNodeIndex: number = -1;

		rules.forEach(rule => {
			if (rule.statement.typeName === 'IfStatement') {
				ruleNodeIndex++;
			}
			if (ruleNodes[ruleNodeIndex]) {
				ruleNodes[ruleNodeIndex] = [...ruleNodes[ruleNodeIndex], rule];
			} else {
				ruleNodes.push([rule]);
			}
		});

		const ruleNodeEngines: IRuleEngines[] = [];

		ruleNodes.forEach(node => {
			ruleNodeEngines.push(setRuleEngines(node));
		});

		const facts: IDictionary<any> = {};

		allFactKeys = [...new Set(allFactKeys)];

		const nodeResults: boolean[] = [];
		const actionsToMobile: RuleEngineLegacyEntities.IBaseAction[] = [];

		for (const i in ruleNodeEngines) {
			try {
				let formData: Map<string, LayoutCreatorEntities.LayoutItem>;
				const state: BaseState = yield select();
				if (args.checkType === 'execution') {
					formData = getFormItems(state, args.windowId) as Map<
						string,
						LayoutCreatorEntities.LayoutDataItem
					>;
				} else if (args.checkType === 'preview') {
					formData = getPreviewFormItems(state as MainState, args.windowId);
				}

				allFactKeys.forEach(factKey => {
					const controlName = factKey.split('.')[0];
					const propKey = factKey
						.split('.')
						.splice(1, factKey.split('.').length + 1)
						.join('.');

					const fact = getControlPropertyValue({
						controlId: controlName,
						keys: propKey,
						data: formData,
						checkType: args.checkType,
						FormRuntimeItemManager: null
					});

					facts[factKey] = isNullOrUndefined(fact) ? null : fact;
				});

				for (const ruleId in ruleNodeEngines[i]) {
					//RUN IF STATEMENT FACTS
					let ifStatementProvided = false;

					if (ruleNodeEngines[i][ruleId].if) {
						const enginePromise = ruleNodeEngines[i][ruleId].if.run(facts);
						const results: EngineResult = yield enginePromise;

						if (results?.events?.length) {
							ifStatementProvided = true;
							for (const resultItem in results.events) {
								results.events[resultItem].params.actions.forEach((i: any) =>
									actionsToMobile.push(i)
								);
							}
							result = yield triggerStatement(results, args, result);
						}
					}

					//RUN IF STATEMENT FACTS : END
					//RUN ELSE IF STATEMENT FACTS

					let elseIfStatementProvided = false;

					if (!ifStatementProvided && ruleNodeEngines[i][ruleId].elseif) {
						const elseIfStatements = ruleNodeEngines[i][ruleId].elseif;
						for (const statementId in elseIfStatements) {
							if (!elseIfStatementProvided) {
								const enginePromise = elseIfStatements[statementId].run(facts);
								const results: EngineResult = yield enginePromise;

								if (results?.events?.length) {
									elseIfStatementProvided = true;
									for (const resultItem in results.events) {
										results.events[resultItem].params.actions.forEach(
											(i: any) => actionsToMobile.push(i)
										);
									}
									result = yield triggerStatement(results, args, result);
								}
							}
						}
					}

					//RUN ELSE IF STATEMENT FACTS : END

					//RUN ELSE STATEMENT FACTS

					if (!ifStatementProvided && !elseIfStatementProvided) {
						if (ruleNodeEngines[i][ruleId].else) {
							const enginePromise = ruleNodeEngines[i][ruleId].else.run(facts);
							const results: EngineResult = yield enginePromise;

							if (results?.events?.length) {
								for (const resultItem in results.events) {
									results.events[resultItem].params.actions.forEach((i: any) =>
										actionsToMobile.push(i)
									);
								}
								result = yield triggerStatement(results, args, result);
							} else {
								result = false;
							}
						} else {
							result = false;
						}
					}
					//RUN ELSE STATEMENT FACTS : END
				}
			} catch (e) {
				console.error(e);
				result = true;
			}

			nodeResults.push(result);
		}

		// #endregion
		result = nodeResults.filter(Boolean).length > 0;
	}

	if (
		RxEventEmitter.subjects[`$${args.windowId}`] ||
		RxEventEmitter.subjects[`$checkRules_${args.windowId}`]
	) {
		RxEventEmitter.emit('checkRules_' + args.windowId, {
			type: 'CHECK_RULES_RESULT',
			windowId: args.windowId,
			result: result
		});
	}

	return result;
}
